import type { FC } from 'react';
import { Chart } from 'react-chartjs-2';

import {
  BarController,
  BarElement,
  CategoryScale,
  type ChartData,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-moment';
import { formatDate } from 'enable-ui';

import { COLOR, COLOR_CONFIG } from 'constants/color';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  TimeScale,
  TimeSeriesScale,
);

export const MixedChart: FC<{
  data: ChartData<'bar' | 'line', number[], string>;
  isHidden?: [boolean, boolean];
  setIsHidden?: React.Dispatch<React.SetStateAction<[boolean, boolean]>>;
}> = ({ data, setIsHidden, isHidden }) => {
  return (
    <Chart
      type="bar"
      data={data}
      options={{
        plugins: {
          legend: {
            display: true,
            labels: {
              usePointStyle: true,
              boxWidth: 5,
              boxHeight: 5,
              font: {
                size: 14,
              },
              padding: 24,

              generateLabels: (chart) => {
                return chart.data.datasets.map((item, key) => {
                  return {
                    datasetIndex: key,
                    text: item.label || '',
                    strokeStyle: item.label === 'Score' ? COLOR.primary['primary-6'] : '#49A3F1',
                    fillStyle: item.label === 'Score' ? COLOR.primary['primary-6'] : '#49A3F1',
                    pointStyle: item.type === 'line' ? 'line' : 'circle',
                    backgroundColor:
                      item.label === 'Score' ? COLOR.primary['primary-6'] : '#49A3F1',
                    fontColor: item.hidden
                      ? COLOR_CONFIG['neutral-text']
                      : COLOR.surfaceNeutralBold['neutral-11'],
                    lineWidth: 5,
                    lineCap: 'round',
                  };
                });
              },
            },
            onHover: (event, legendItem, legend) => {
              if (event.native && event.native.target) {
                const target = event.native.target as HTMLElement;

                target.style.cursor = 'pointer';
              }
            },
            onClick: (_event, legendItem, _legend) => {
              if (!setIsHidden || !isHidden) return;
              const index = legendItem.datasetIndex as number;

              if (index === 0) {
                setIsHidden([!isHidden[0], isHidden[1]]);
              } else {
                setIsHidden([isHidden[0], !isHidden[1]]);
              }
            },
            onLeave: (event, legendItem, legend) => {
              if (event.native && event.native.target) {
                const target = event.native.target as HTMLElement;

                target.style.cursor = 'default';
              }
            },
          },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: (context) => {
                return formatDate(context[0].label, 'd MMM');
              },
            },
          },
        },
        backgroundColor: COLOR.primary['primary-6'],

        scales: {
          x: {
            grid: {
              display: false,
            },
            type: 'time',
            time: {
              unit: 'day',
              displayFormats: {
                day: 'MMM D',
              },
            },
          },
          y: {
            beginAtZero: true,
            type: 'linear',
            position: 'right',
            grid: {
              display: false,
            },
            min: 0,
            ticks: { stepSize: 20 },
            title: {
              display: true,
              text: '% of max score',
            },
          },
          y1: {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: 'Score',
            },
            grid: {
              display: true,
            },
          },
        },
      }}
    />
  );
};
